import React, { useEffect, useState, useMemo } from 'react';
import { Doctor, DoctorService } from '../../../services/DoctorService';
import SearchIcon from '@mui/icons-material/Search';
import { 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthService } from 'services/AuthService';
import AddDoctor from './AddDoctor';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

const limit = 7;

const DoctorList: React.FC = () => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [skip, setSkip] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState<Doctor | null>(null);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const fetchedDoctors = await DoctorService.getAllDoctors({
        search: searchTerm,
        skip,
        limit
      });
      setDoctors(fetchedDoctors);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchDoctors();
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, skip]);

  const filteredDoctors = useMemo(() => {
    return doctors.filter((doctor) => {
      const matchesSearch =
        doctor.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doctor.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [doctors, searchTerm]);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setSelectedDoctor(null);
    setOpenDialog(false);
  };
  
  const handleDoctorAdded = (updatedDoctor: Doctor) => {
    if (selectedDoctor) {
      setDoctors(doctors.map(doctor => doctor.id === updatedDoctor.id ? updatedDoctor : doctor));
      setUpdateSuccessOpen(true);
    } else {
      setDoctors([...doctors, updatedDoctor]);
    }
    handleCloseDialog();
    fetchDoctors();
  };

  const handleEditDoctor = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setOpenDialog(true);
  };

  const handleDeleteDoctor = (id: number) => {
    const doctor = doctors.find(d => d.id === id);
    if (doctor) {
      setDoctorToDelete(doctor);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (doctorToDelete) {
      try {
        await AuthService.deleteUser(doctorToDelete.id);
        setDoctors(doctors.filter(doctor => doctor.id !== doctorToDelete.id));
        setDeleteDialogOpen(false);
        setDeleteSuccessOpen(true);
        fetchDoctors();
      } catch (error) {
        console.error('Error deleting doctor:', error);
      }
    }
  };

  const handleCloseDeleteSuccess = () => {
    setDeleteSuccessOpen(false);
  };

  const handleCloseUpdateSuccess = () => {
    setUpdateSuccessOpen(false);
  };

  return (
    <Box sx={{ padding: '12px', margin: '0 auto' }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.8rem' },
            textAlign: { xs: 'center', md: 'left' },
            pl: { xs: 0, md: 1 },
          }}
        >
          Doctor Management
        </Typography>
      
        <Button
          onClick={handleOpenDialog}
          startIcon={<AddIcon />}
          disabled={loading}
          sx={{
            backgroundColor: '#7287FC',
            color: '#FFFFFF',
            fontWeight: 500,
            fontSize: '0.95rem',
            textTransform: 'none',
            borderRadius: '10px',
            padding: '8px 22px',
            boxShadow: 'none',
            minWidth: '160px',
            textAlign: 'center',
            '&:hover': {
              backgroundColor: '#5b6de0',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
            },
            '&:active': {
              backgroundColor: '#4f60c9',
            },
          }}
        >
          Add Doctor
        </Button>
      </Box>

      {/* Search Field */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          placeholder="Search..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setSkip(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: '100%', sm: 250 },
            flexGrow: 1,
          }}
        />
      </Box>

      {/* Table Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Prénom</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Adresse E-mail</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Rôle</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredDoctors.length > 0 ? (
                filteredDoctors.map((doctor) => (
                  <TableRow key={doctor.id} hover>
                    <TableCell>{doctor.nom || 'N/A'}</TableCell>
                    <TableCell>{doctor.prenom || 'N/A'}</TableCell>
                    <TableCell>{doctor.email || 'N/A'}</TableCell>
                    <TableCell>{doctor.role || 'N/A'}</TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handleEditDoctor(doctor)}
                        size="small"
                        sx={{ color: '#26A69A' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteDoctor(doctor.id)}
                        size="small"
                        sx={{ color: '#EF5350' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No doctors found
                    </Typography>
                    <Button 
                      variant="contained" 
                      onClick={fetchDoctors}
                      sx={{ mt: 2 }}
                    >
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination Controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3,
        gap: 2
      }}>
        <Button
          variant="contained"
          onClick={() => setSkip((prev) => Math.max(prev - limit, 0))}
          disabled={skip === 0 || loading}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          onClick={() => setSkip((prev) => prev + limit)}
          disabled={doctors.length < limit || loading}
        >
          Next
        </Button>
      </Box>

      {/* Add Doctor Dialog */}
      <AddDoctor
        open={openDialog}
        onClose={handleCloseDialog}
        onDoctorAdded={handleDoctorAdded}
        doctor={selectedDoctor ?? undefined}
        isEditing={!!selectedDoctor}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: '8px', sm: '12px', md: '16px' },
            minWidth: { xs: '90vw', sm: 360, md: 400 },
            maxWidth: '95vw',
            maxHeight: '95vh',
            backgroundColor: '#fff',
            boxShadow: 24,
            mx: 2,
            my: 2,
            overflowY: 'auto',
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: { xs: 3, sm: 4, md: 5 },
            pb: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.7rem' },
              fontWeight: 700,
              color: 'error.main',
              mb: 1,
              lineHeight: 1.3,
            }}
          >
            Confirm Deletion
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.05rem' },
              fontWeight: 400,
              color: 'text.secondary',
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete {doctorToDelete?.nom} {doctorToDelete?.prenom}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: { xs: 2, sm: 3, md: 4 },
            gap: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              minWidth: { xs: '45%', sm: 120 },
              height: { xs: 36, sm: 40, md: 44 },
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              textTransform: 'none',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={loading}
            variant="contained"
            color="error"
            sx={{
              minWidth: { xs: '45%', sm: 120 },
              height: { xs: 36, sm: 40, md: 44 },
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              textTransform: 'none',
              boxShadow: 'none',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Success Dialog */}
      <Dialog
        open={deleteSuccessOpen}
        onClose={handleCloseDeleteSuccess}
        PaperProps={{
          sx: {
            borderRadius: { xs: '8px', sm: '12px', md: '16px' },
            minWidth: { xs: '90vw', sm: 360, md: 400 },
            maxWidth: '95vw',
            maxHeight: '95vh',
            backgroundColor: '#fff',
            boxShadow: 24,
            mx: 2,
            my: 2,
            overflowY: 'auto',
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: { xs: 3, sm: 4, md: 5 },
            pb: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              color: 'success.main',
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.7rem' },
              fontWeight: 700,
              color: 'success.dark',
              mb: 1,
              lineHeight: 1.3,
            }}
          >
            Deleted Successfully
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.05rem' },
              fontWeight: 400,
              color: 'text.secondary',
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            The doctor has been removed from the list.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: { xs: 2, sm: 3, md: 4 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Button
            onClick={handleCloseDeleteSuccess}
            variant="contained"
            color="primary"
            sx={{
              minWidth: { xs: '90%', sm: 160 },
              height: { xs: 36, sm: 40, md: 44 },
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              textTransform: 'none',
              boxShadow: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Success Dialog */}
      <Dialog
        open={updateSuccessOpen}
        onClose={handleCloseUpdateSuccess}
        PaperProps={{
          sx: {
            borderRadius: { xs: '12px', sm: '16px', md: '20px' },
            minWidth: { xs: 280, sm: 320, md: 360 },
            maxWidth: '90vw',
            backgroundColor: '#fff',
            boxShadow: 6,
            mx: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: { xs: 4, sm: 5, md: 6 },
            pb: { xs: 2, sm: 3 },
            px: { xs: 3, sm: 4 },
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem' },
              color: '#22c55e',
              mb: { xs: 1.5, sm: 2 },
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
              fontWeight: 700,
              color: '#1D1D4E',
              mb: { xs: 0.5, sm: 1 },
              fontFamily: 'Poppins, sans-serif',
              lineHeight: 1.3,
            }}
          >
            Doctor Updated Successfully
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              fontWeight: 400,
              color: '#7A7E9F',
              fontFamily: 'Poppins, sans-serif',
              mb: { xs: 1.5, sm: 2 },
              lineHeight: 1.5,
            }}
          >
            The doctor's information has been updated.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: { xs: 3, sm: 4, md: 5 },
            gap: 2,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Button
            onClick={handleCloseUpdateSuccess}
            sx={{
              minWidth: { xs: 120, sm: 140, md: 160 },
              height: { xs: 40, sm: 44, md: 48 },
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: '#7287FC',
              color: '#fff',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', md: '1rem' },
              textTransform: 'none',
              fontFamily: 'Poppins, sans-serif',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#5a6be0',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorList;